import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { persistReducer } from "redux-persist";

import storage from "redux-persist/lib/storage";

import { ClientConfig, defaultClientConfig } from "~/config/clientConfig";
import { parseClientConfigJsonSafely } from "~/lib/helpers";
import { clientConfigApi } from "~/redux/warehouse/clientConfig";

export interface AppState {
  ready: {
    flags: {
      clientId: boolean;
      fcId: boolean;
    };
    authHeader: boolean;
    clientConfig: boolean;
    signalr: {
      main: boolean;
      andon: boolean;
    };
  };
  clientConfig: ClientConfig;
  clientConfigError: string | null;
  improvedErrorHandlingConfigEnabled: boolean;
  siteLoginPath: string | null;
  signalREnabled: boolean;
}

export const initialState: AppState = {
  ready: {
    flags: {
      clientId: false,
      fcId: false
    },
    authHeader: false,
    clientConfig: false,
    signalr: {
      main: false,
      andon: false
    }
  },
  clientConfig: defaultClientConfig,
  clientConfigError: null,
  improvedErrorHandlingConfigEnabled: false,
  siteLoginPath: null,
  signalREnabled: false
};

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setSiteLoginPath(state, action: PayloadAction<string | null>) {
      state.siteLoginPath = action.payload;
    },
    setFlagsReadyFcId(state, action: PayloadAction<boolean>) {
      state.ready.flags.fcId = action.payload;
    },
    setFlagsReadyClientId(state, action: PayloadAction<boolean>) {
      state.ready.flags.clientId = action.payload;
    },
    setAuthHeaderReady(state, action: PayloadAction<boolean>) {
      state.ready.authHeader = action.payload;
    },
    setSignalRReadyMain(state, action: PayloadAction<boolean>) {
      state.ready.signalr.main = action.payload;
    },
    setSignalRReadyAndon(state, action: PayloadAction<boolean>) {
      state.ready.signalr.andon = action.payload;
    },
    resetAppReady(state) {
      state.ready = {
        flags: {
          clientId: false,
          fcId: false
        },
        authHeader: false,
        clientConfig: false,
        signalr: {
          main: false,
          andon: false
        }
      };
    },
    setClientConfig(state, action: PayloadAction<ClientConfig>) {
      state.clientConfig = action.payload;
    },
    clearClientConfigError(state) {
      state.clientConfigError = null;
    },
    setImprovedErrorHandlingConfigEnabled(
      state,
      action: PayloadAction<boolean>
    ) {
      state.improvedErrorHandlingConfigEnabled = action.payload;
    },
    setSignalREnabled(state, action: PayloadAction<boolean>) {
      state.signalREnabled = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      clientConfigApi.endpoints.getClientConfig.matchFulfilled,
      (state, action) => {
        if (!state.improvedErrorHandlingConfigEnabled) {
          const parsedConfig =
            (parseClientConfigJsonSafely(action.payload) as ClientConfig) ||
            defaultClientConfig;
          state.clientConfig = parsedConfig;
          state.clientConfigError = null;
          state.ready.clientConfig = true;
          return;
        }

        const rawParsed = parseClientConfigJsonSafely(action.payload);
        const hadConfigurationString = Boolean(
          action.payload?.clientConfiguration
        );
        const unexpected = rawParsed === null && hadConfigurationString;

        if (unexpected) {
          state.clientConfig = defaultClientConfig;
          state.clientConfigError =
            "Client configuration could not be read from the server";
          state.ready.clientConfig = false;
          return;
        }

        const parsedConfig = (rawParsed as ClientConfig) || defaultClientConfig;
        state.clientConfig = parsedConfig;
        state.clientConfigError = null;
        state.ready.clientConfig = true;
      }
    );
    builder.addMatcher(
      clientConfigApi.endpoints.getClientConfig.matchRejected,
      (state, action) => {
        if (action.meta.aborted && state.improvedErrorHandlingConfigEnabled) {
          return;
        }
        state.clientConfig = defaultClientConfig;
        state.clientConfigError =
          action.error?.message ?? "Failed to load client configuration";
        state.ready.clientConfig = false;
      }
    );
  }
});
const appPersistConfig = {
  key: "app",
  storage,
  version: 1,
  whitelist: ["siteLoginPath"]
};

export const {
  setSiteLoginPath,
  setFlagsReadyFcId,
  setFlagsReadyClientId,
  setAuthHeaderReady,
  setSignalRReadyMain,
  setSignalRReadyAndon,
  resetAppReady,
  setClientConfig,
  clearClientConfigError,
  setImprovedErrorHandlingConfigEnabled,
  setSignalREnabled
} = appSlice.actions;

export const app = persistReducer(appPersistConfig, appSlice.reducer);
